import type {PaymentCard} from '@yandex-delivery/api/model/payment'
import {type ButtonProps, Typography, useTransition} from '@yandex-delivery/blocks'
import {useI18n} from '@yandex-delivery/core/i18n'
import {SafeHTML} from '@yandex-delivery/xss-defender'
import {type PropsWithChildren, useState} from 'react'

import type {Props} from './PaymentCardList.types'
import {buildKey} from './utils'

type State = {
  card?: PaymentCard
  agreement?: string
  notificationsAgreement?: string
  checked: boolean
  isNotificationsAllowed?: boolean
}
type AgreementProps = PropsWithChildren<{
  error?: string
  checked: boolean
  onChange: VoidFunction
}>

export function usePaymentCardList(props: Props) {
  const t = useI18n()
  const {
    cards,
    card: cardProp,
    agreement: agreementProp,
    notificationsAgreement: notificationsAgreementProp,
    isNotificationsInteracted,
    fetchBoundCard,
    onSuccess,
    title = t('card_list_modal.select_payment_method'),
    subtitle = t('card_list_modal.bound_message'),
    isCheckedOfferAgreement,
  } = props
  const [{card, agreement, notificationsAgreement, checked, isNotificationsAllowed}, setState] = useState<State>({
    card: cardProp,
    agreement: agreementProp,
    notificationsAgreement: notificationsAgreementProp,
    checked: isCheckedOfferAgreement,
    isNotificationsAllowed: false,
  })
  const [{isPending, error}, startTransition] = useTransition<PaymentCard>(undefined, {key: buildKey('bound')})

  const translates = {
    title,
    subtitle,
    button: isPending
      ? t('phoenix.add_new_card.pending', 'Привязываем карту...')
      : error
      ? t('phoenix.add_new_card.error', 'Не удалось привязать карту, повторить?')
      : t('phoenix.add_new_card.submit'),
  } as const

  function changeHandler(nextCard: PaymentCard) {
    setState(prevState => ({
      ...prevState,
      card: nextCard.id === prevState.card?.id ? undefined : nextCard,
    }))
  }

  const buttonProps: ButtonProps = {
    disabled: !card || (isNotificationsInteracted ? false : !checked),
    variant: isPending ? 'default' : error ? 'error' : 'action',
    loading: isPending,
    onPress:
      card && (checked || isNotificationsInteracted)
        ? () => {
            startTransition(() => fetchBoundCard(card, notificationsAgreementProps?.checked), {
              onCommit: onSuccess,
            })
          }
        : undefined,
  }
  const agreementProps: AgreementProps | undefined = agreement
    ? {
        error: !checked ? t('profile.signup.validation.checkbox') : undefined,
        checked,
        children: (
          <Typography variant="body2">
            <SafeHTML html={agreement} />
          </Typography>
        ),
        onChange() {
          setState(prevState => ({
            ...prevState,
            checked: !prevState.checked,
          }))
        },
      }
    : undefined

  const notificationsAgreementProps: AgreementProps | undefined = notificationsAgreement
    ? {
        checked: !!isNotificationsAllowed,
        children: (
          <Typography variant="body2">
            <SafeHTML html={notificationsAgreement} />
          </Typography>
        ),
        onChange() {
          setState(prevState => ({
            ...prevState,
            isNotificationsAllowed: !prevState.isNotificationsAllowed,
          }))
        },
      }
    : undefined

  return {
    cards,
    currentCard: card,
    changeHandler,
    translates,
    buttonProps,
    agreementProps,
    notificationsAgreementProps,
  } as const
}
